import { DiskLinkError, parseDiskLink } from 'modules/match-video/domain/utils';
import { YandexDiskError, YandexDiskErrorCode, yandexDiskPublicRepository } from 'modules/match-video/domain/repositories';
import { useCallback, useEffect, useRef, useState } from 'react';

export type DiskFolderStatus = 'idle' | 'listing' | 'downloading' | 'failed';

/** Why the folder gave no clips: a wrong link, a Disk refusal, or a folder without videos. */
export type DiskFolderFailure = DiskLinkError | YandexDiskErrorCode | 'empty';

export interface DiskFolderProgress {
  /** 1-based number of the file being downloaded. */
  fileNumber: number;
  fileCount: number;
  /** 0…1 over all the bytes of the folder's videos. */
  share: number;
}

export interface DiskFolderClips {
  status: DiskFolderStatus;
  failure: DiskFolderFailure | null;
  progress: DiskFolderProgress | null;
  /** Lists and downloads the folder's videos; resolves with them, or with `null` on failure or cancel. */
  load: (link: string) => Promise<File[] | null>;
  cancel: () => void;
}

const isAbort = (error: unknown) => (error as Error | undefined)?.name === 'AbortError';

/**
 * Takes clips from a public Yandex Disk folder: the files are downloaded into memory one by one and handed out as
 * `File`, so the assembler reads them like files from the computer. Leaving the screen cancels the download.
 */
export function useDiskFolderClips(): DiskFolderClips {
  const [status, setStatus] = useState<DiskFolderStatus>('idle');
  const [failure, setFailure] = useState<DiskFolderFailure | null>(null);
  const [progress, setProgress] = useState<DiskFolderProgress | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStatus('idle');
    setProgress(null);
  }, []);

  useEffect(() => () => abortRef.current?.abort(), []);

  const load = useCallback(async (link: string) => {
    abortRef.current?.abort();
    setFailure(null);
    setProgress(null);

    const parsed = parseDiskLink(link);
    if (!parsed.ok) {
      setFailure(parsed.error);
      setStatus('failed');
      return null;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;
    try {
      setStatus('listing');
      const videos = await yandexDiskPublicRepository.listFolderVideos(parsed.publicKey, signal);
      if (videos.length === 0) {
        setFailure('empty');
        setStatus('failed');
        return null;
      }

      setStatus('downloading');
      const totalBytes = videos.reduce((sum, video) => sum + video.size, 0) || 1;
      let doneBytes = 0;
      const files: File[] = [];
      for (let index = 0; index < videos.length; index++) {
        const video = videos[index];
        const bytesBefore = doneBytes;
        const report = (loaded: number) =>
          setProgress({
            fileNumber: index + 1,
            fileCount: videos.length,
            share: Math.min(1, (bytesBefore + loaded) / totalBytes),
          });
        report(0);
        const blob = await yandexDiskPublicRepository.downloadFile(parsed.publicKey, video.path, {
          signal,
          onProgress: report,
        });
        files.push(new File([blob], video.name, { type: video.mimeType }));
        doneBytes += video.size;
      }

      setStatus('idle');
      setProgress(null);
      return files;
    } catch (error) {
      if (isAbort(error) || signal.aborted) {
        return null;
      }
      console.error(error);
      setFailure(error instanceof YandexDiskError ? error.code : 'http');
      setStatus('failed');
      setProgress(null);
      return null;
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
    }
  }, []);

  return { status, failure, progress, load, cancel };
}
